import { useEffect, useState, type HTMLAttributes, type ReactNode } from 'react';
import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { Map as MapIcon, IdCard as IdCardIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import NumDisplay from '@/components/v2/NumDisplay';
import Eyebrow from '@/components/v2/Eyebrow';
import { useAuth } from '@/contexts/AuthContext';
import { useTokenBalance } from '@/lib/v2/useTokenBalance';
import { SUBSCRIPTION_MODE, planName } from '@/lib/subscription';

export interface DesktopSidebarItem {
  id: string;
  label: string;
  href: string;
  icon: ReactNode;
  /**
   * Section root used to decide the active highlight, when the link opens a
   * page DEEPER than its section (CV opens /cvs/new but owns all of /cvs).
   * Defaults to `href`.
   */
  matchHref?: string;
}

export interface DesktopSidebarProps extends HTMLAttributes<HTMLElement> {
  /** override default nav items */
  items?: DesktopSidebarItem[];
  /** show the token balance widget. Defaults to true. */
  showTokenWidget?: boolean;
  /** token balance to render in the widget. If omitted, read from profile. */
  balance?: number;
  /** total token allowance. If omitted, derive from plan. */
  total?: number;
  /** user shortcut at the bottom — name. If omitted, read from profile. */
  userName?: string;
  /** user shortcut — plan label. If omitted, derive from profile.plan. */
  userPlan?: string;
}

// Plan keys, labels and monthly token quotas mirror the canonical `plans`
// table in Supabase (free / starter / growth / enterprise). Keep in sync if
// pricing changes there. Legacy pro/elite aliases map to the closest tier so
// older profiles still render a sensible label instead of falling back to free.
const PLAN_QUOTAS: Record<string, number> = {
  free: 10,
  starter: 200,
  growth: 500,
  enterprise: 0,
  // legacy aliases
  pro: 200,
  elite: 500,
};

const PLAN_LABELS: Record<string, string> = {
  free: 'استكشف',
  starter: 'الانطلاق',
  growth: 'النمو',
  enterprise: 'المؤسسات',
  // legacy aliases
  pro: 'الانطلاق',
  elite: 'النمو',
};

function firstNameOf(full: string | null | undefined, email: string | null | undefined): string {
  const trimmed = (full ?? '').trim();
  if (trimmed) return trimmed.split(/\s+/)[0]!;
  const local = (email ?? '').split('@')[0];
  return local || '';
}

const HomeIcon = (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M3 8 L10 3 L17 8 V16 H3 Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
  </svg>
);
const ShieldIcon = (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M10 2 L17 5 V10 c0 4 -3 6 -7 8 c-4 -2 -7 -4 -7 -8 V5 Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
  </svg>
);
const RadarIcon = (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.3" />
    <circle cx="10" cy="10" r="4" stroke="currentColor" strokeWidth="1.3" />
    <circle cx="10" cy="10" r="1" fill="currentColor" />
  </svg>
);
const PostsIcon = (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M3 4 H14 M3 8 H17 M3 12 H14 M3 16 H17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);
const ActivityIcon = (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M3 11 L7 11 L9 5 L12 16 L14 9 L17 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const CvIcon = (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <rect x="4" y="2" width="12" height="16" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M7 7 H13 M7 10 H13 M7 13 H10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);
const LogOutIcon = (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M8 17 H5 a1.5 1.5 0 01-1.5-1.5 V4.5 A1.5 1.5 0 015 3 H8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12.5 13.5 L16 10 L12.5 6.5 M16 10 H8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CoverLetterIcon = (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <rect x="4" y="2" width="12" height="16" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
    <path d="M7 7 h6 M7 10 h6 M7 13 h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

// خطتي — the living Career Plan pillar (ADR-006).
const PlanIcon = <MapIcon size={18} strokeWidth={1.4} aria-hidden="true" />;
// ملفي المهني — the unified Career Brain (view / edit / upload).
const ProfileIcon = <IdCardIcon size={18} strokeWidth={1.4} aria-hidden="true" />;

function buildDefaultItems(isAr: boolean): DesktopSidebarItem[] {
  return [
    { id: 'home',     label: isAr ? 'رحلتي'      : 'My Journey',    href: '/home',           icon: HomeIcon },
    { id: 'plan',     label: isAr ? 'خطتي'       : 'My Plan',       href: '/plan',           icon: PlanIcon },
    { id: 'analyze',  label: isAr ? 'الرادار'    : 'Radar',         href: '/analyze',        icon: RadarIcon },
    // «السيرة» opens the builder directly (/cvs/new) instead of the list — the
    // list was an extra stop between the user and the thing they came to do.
    // matchHref keeps the item highlighted across every /cvs/* page.
    { id: 'cv',       label: isAr ? 'السيرة'     : 'CV',            href: '/cvs/new',        icon: CvIcon, matchHref: '/cvs' },
    { id: 'cover',    label: isAr ? 'خطاب التغطية' : 'Cover Letter', href: '/cover-letter',   icon: CoverLetterIcon },
    { id: 'posts',    label: isAr ? 'المنشورات'  : 'Posts',         href: '/posts',          icon: PostsIcon },
    { id: 'profile',  label: isAr ? 'ملفي المهني' : 'My Profile',   href: '/settings/career', icon: ProfileIcon },
  ];
}

function DesktopSidebar({
  className,
  items: itemsProp,
  showTokenWidget = true,
  balance: balanceProp,
  total: totalProp,
  userName: userNameProp,
  userPlan: userPlanProp,
  ...rest
}: DesktopSidebarProps) {
  const [location, navigate] = useLocation();
  const { user, profile, signOut } = useAuth();
  const { t, i18n } = useTranslation();
  const isAr = (i18n.language || 'ar').startsWith('ar');

  // Same sign-out flow as UserMenu: clear the session, then land on the
  // public landing page (replace so Back doesn't return to the gated app).
  const handleSignOut = async () => {
    await signOut();
  };

  const planKey = profile?.plan ?? 'free';
  // Unified token source (3-wallet total via get_wallets_v2) — same number the
  // dashboard, Profile, and Billing show. An explicit `balance` prop still wins
  // for callers that already hold the value. While the wallet loads we fall back
  // to the legacy profile field so the widget isn't blank on first paint.
  const wallet = useTokenBalance();
  const balance = balanceProp ?? wallet.total ?? profile?.token_balance ?? 0;
  const total = totalProp ?? PLAN_QUOTAS[planKey] ?? PLAN_QUOTAS.free;
  const userName = userNameProp ?? firstNameOf(profile?.full_name, profile?.email ?? user?.email);
  const userPlanLabelsEn: Record<string, string> = {
    free: 'Explore', starter: 'Starter', growth: 'Growth', enterprise: 'Enterprise',
    // legacy aliases
    pro: 'Starter', elite: 'Growth',
  };
  const userPlan = userPlanProp ?? (isAr ? (PLAN_LABELS[planKey] ?? PLAN_LABELS.free) : (userPlanLabelsEn[planKey] ?? userPlanLabelsEn.free));
  const avatarUrl = profile?.avatar_url ?? null;
  const [avatarBroken, setAvatarBroken] = useState(false);
  useEffect(() => { setAvatarBroken(false); }, [avatarUrl]);

  const defaultItems = buildDefaultItems(isAr);
  const items: DesktopSidebarItem[] = itemsProp ?? (
    profile?.is_admin
      ? [...defaultItems, { id: 'admin', label: isAr ? 'الإدارة' : 'Admin', href: '/admin', icon: ShieldIcon }]
      : defaultItems
  );

  // The /total bar was removed in favor of a clean balance display, but we
  // still compute `total` for potential future plan-quota UI.
  void total;

  return (
    <aside
      aria-label={isAr ? 'التنقل الجانبي' : 'Sidebar navigation'}
      className={cn(
        'sticky top-16 flex h-[calc(100dvh-64px)] w-60 shrink-0 flex-col',
        // border-e = end side (left in RTL, right in LTR) — the edge that meets <main>
        'border-e border-v2-line bg-v2-surface',
        'animate-[v2-sidebar-in_300ms_var(--ease-out)_both]',
        className,
      )}
      {...rest}
    >
      <style>{`
        @keyframes v2-sidebar-in {
          0%   { opacity: 0; transform: translateX(8px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        :root[dir="rtl"] aside[aria-label="التنقل الجانبي"] {
          animation-name: v2-sidebar-in-rtl;
        }
        @keyframes v2-sidebar-in-rtl {
          0%   { opacity: 0; transform: translateX(-8px); }
          100% { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      <nav className="flex flex-col gap-0.5 p-3" aria-label={isAr ? 'القوائم الرئيسية' : 'Primary navigation'}>
        {items.map((item) => {
          const matchHref = item.matchHref ?? item.href;
          const isActive = location === matchHref || location.startsWith(`${matchHref}/`);
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => navigate(item.href)}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'group relative flex items-center gap-3 h-10 ps-3 pe-3 rounded-v2-sm cursor-pointer',
                'font-ar text-[14px] transition-colors duration-200 ease-out',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/30',
                isActive
                  ? 'bg-teal-50 text-teal-700 font-semibold'
                  : 'text-v2-body hover:bg-v2-canvas-2 hover:text-v2-ink font-medium',
              )}
            >
              {/* RTL: active indicator on the start (right) edge — 4px */}
              {isActive && (
                <span
                  aria-hidden="true"
                  className="absolute inset-y-1 start-0 w-[3px] rounded-full bg-teal-600"
                />
              )}
              <span className={cn('shrink-0', isActive ? 'text-teal-700' : 'text-v2-mute group-hover:text-v2-body')}>
                {item.icon}
              </span>
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="my-1 mx-3 h-px bg-v2-line" />

      {showTokenWidget && (
        <div className="px-3 py-2">
          <div className="rounded-v2-md border border-v2-line bg-v2-canvas px-3.5 py-3">
            {SUBSCRIPTION_MODE ? (
              <>
                <Eyebrow>{isAr ? 'باقتك' : 'Your plan'}</Eyebrow>
                <div className="mt-1.5 font-ar text-[15px] font-bold leading-none text-v2-ink">
                  {planName(planKey, isAr)}
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/billing')}
                  className="mt-2 font-ar text-[12px] font-semibold text-teal-700 hover:text-teal-600 cursor-pointer"
                >
                  {isAr ? 'إدارة الاشتراك' : 'Manage plan'}
                </button>
              </>
            ) : (
              <>
                <Eyebrow>{t('wallets.sidebar.balanceTitle')}</Eyebrow>
                <div className="mt-1.5 flex items-baseline gap-1.5">
                  <NumDisplay className="text-[20px] font-bold leading-none text-v2-ink">
                    {balance}
                  </NumDisplay>
                  <span className="font-ar text-[11px] text-v2-dim">{t('wallets.sidebar.unit')}</span>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/billing?topup=1')}
                  className="mt-2 font-ar text-[12px] font-semibold text-teal-700 hover:text-teal-600 cursor-pointer"
                >
                  {t('wallets.sidebar.topup')}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Spacer pushes the user shortcut to the bottom */}
      <div className="flex-1" />

      <button
        type="button"
        onClick={() => navigate('/me')}
        className={cn(
          'm-3 flex items-center gap-2.5 rounded-v2-sm border border-v2-line bg-v2-surface px-2.5 py-2',
          'text-start cursor-pointer hover:bg-v2-canvas-2 transition-colors duration-200 ease-out',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/30',
        )}
        aria-label={`${isAr ? 'الحساب، ' : 'Account, '}${userName}`}
      >
        {avatarUrl && !avatarBroken ? (
          <img
            src={avatarUrl}
            alt=""
            className="h-8 w-8 shrink-0 rounded-full object-cover"
            aria-hidden="true"
            referrerPolicy="no-referrer"
            onError={() => setAvatarBroken(true)}
          />
        ) : (
          <span
            aria-hidden="true"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-600 font-ar text-[13px] font-bold text-white"
          >
            {userName.trim().charAt(0) || '?'}
          </span>
        )}
        <span className="min-w-0 flex-1 font-ar">
          <span className="block truncate text-[13px] font-semibold text-v2-ink">{userName || (isAr ? 'الحساب' : 'Account')}</span>
          <span className="block truncate text-[11px] text-v2-dim">{userPlan}</span>
        </span>
      </button>

      <button
        type="button"
        onClick={handleSignOut}
        className={cn(
          'mx-3 mb-3 flex items-center gap-3 h-10 ps-3 pe-3 rounded-v2-sm cursor-pointer',
          'font-ar text-[14px] font-medium text-v2-body transition-colors duration-200 ease-out',
          'hover:bg-v2-rose-50 hover:text-v2-rose',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/30',
        )}
      >
        <span className="shrink-0 text-v2-mute">{LogOutIcon}</span>
        <span>{isAr ? 'تسجيل الخروج' : 'Sign out'}</span>
      </button>
    </aside>
  );
}

export default DesktopSidebar;
